import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export const api = {
  // meta
  currencies: () => client.get('/meta/currencies'),
  // site configuration (single source of truth for vertical/entity visibility)
  siteConfig: () => client.get('/site-config'),
  adminSiteConfig: () => client.get('/admin/site-config'),
  updateSiteConfig: (body) => client.put('/admin/site-config', body),
  providers: () => client.get('/meta/providers'),
  seoMeta: (path) => client.get(`/meta/seo/${path}`),
  // airports + search
  airports: (q) => client.get('/airports', { params: { q } }),
  searchFlight: (body) => client.post('/search/flight', body),
  flightStreamUrl: () => `${API}/search/flight/stream`,
  multiFlightStreamUrl: () => `${API}/search/flight/multi/stream`,
  nearbyDates: (params) => client.get('/flights/nearby-dates', { params }),
  monthPrices: (params) => client.get('/flights/month-prices', { params }),
  weekendFares: (params) => client.get('/flights/weekends', { params }),
  flightDiscovery: (params) => client.get('/flights/discovery', { params }),
  countryAirports: (params) => client.get('/flights/country-airports', { params }),
  originCountries: (params) => client.get('/flights/origin-countries', { params }),
  contact: (body) => client.post('/contact', body),
  createFareAlert: (body) => client.post('/fare-alerts', body),
  searchFlightMulti: (body) => client.post('/search/flight/multi', body),
  getFlightSession: (id) => client.get(`/search/flight/${id}`),
  flightBuyUrl: (sessionId, proposalId) => `${API}/flights/buy/${sessionId}/${encodeURIComponent(proposalId)}`,
  smartFareBuyUrl: (oppId) => `${API}/flights/smart-fares/${oppId}/buy`,
  context: (id) => client.get(`/context/${id}`),
  // hotels / experiences / deals / destinations
  hotels: (params) => client.get('/hotels/search', { params }),
  hotelsLocations: (q, opts = {}) => client.get('/hotels/locations', { params: { q, ...opts } }),
  hotelsNameSearch: (q) => client.get('/hotels/name-search', { params: { q } }),
  hotelDetail: (hotelId, params) => client.get(`/hotels/detail/${hotelId}`, { params: params || {} }),
  hotelsSimilar: (hotelId, params) => client.get(`/hotels/similar/${hotelId}`, { params: params || {} }),
  experiences: (params) => client.get('/experiences/search', { params }),
  experienceDetail: (code, params) => client.get(`/experiences/detail/${code}`, { params: params || {} }),
  experienceCategories: () => client.get('/experiences/categories'),
  // curated cross-vertical discovery (pre-search empty states)
  discoveryHotels: () => client.get('/discovery/hotels'),
  discoveryExperiences: (category) => client.get('/discovery/experiences', { params: category ? { category } : {} }),
  deals: (params) => client.get('/deals', { params }),
  destinations: () => client.get('/destinations'),
  destination: (slug) => client.get(`/destinations/${slug}`),
  cacheOffers: (offers) => client.post('/offers/cache_bulk', { offers }),
  // affiliate go link (returns URL to open)
  goUrl: (type, offerId, sessionId) => {
    const q = sessionId ? `?session_id=${sessionId}` : '';
    return `${API}/go/${type}/${offerId}${q}`;
  },
  // ai
  aiPlan: (prompt) => client.post('/ai/plan', { prompt }),
  // analytics
  track: (event, session_id, props) => client.post('/analytics/track', { event, session_id, props }).catch(() => {}),
  // auth
  signup: (body) => client.post('/auth/signup', body),
  login: (body) => client.post('/auth/login', body),
  googleSession: (session_id) => client.post('/auth/session', { session_id }),
  me: () => client.get('/auth/me'),
  logout: () => client.post('/auth/logout'),
  forgotPassword: (body) => client.post('/auth/forgot-password', body),
  resetPassword: (body) => client.post('/auth/reset-password', body),
  verifyEmail: (token) => client.post('/auth/verify-email', { token }),
  resendVerification: () => client.post('/auth/resend-verification'),
  setCurrency: (currency) => client.put('/account/currency', { currency }),
  updateProfile: (body) => client.put('/account/profile', body),
  emailChangeStart: (new_email) => client.post('/account/email/change/start', { new_email }),
  emailChangeVerify: (otp) => client.post('/account/email/change/verify', { otp }),
  // account
  saveItem: (body) => client.post('/account/saved', body),
  listSaved: () => client.get('/account/saved'),
  deleteSaved: (id) => client.delete(`/account/saved/${id}`),
  saveTrip: (body) => client.post('/account/trips', body),
  listTrips: () => client.get('/account/trips'),
  listSearches: () => client.get('/account/searches'),
  createAlert: (body) => client.post('/account/price-alerts', body),
  listAlerts: () => client.get('/account/price-alerts'),
  deleteAlert: (id) => client.delete(`/account/price-alerts/${id}`),
  // admin
  adminOverview: () => client.get('/admin/overview'),
  adminFunnel: () => client.get('/admin/funnel'),
  adminEvents: (limit = 50) => client.get('/admin/events', { params: { limit } }),
  adminClicks: (limit = 50) => client.get('/admin/clicks', { params: { limit } }),
  adminUsers: (q) => client.get('/admin/users', { params: q ? { q } : {} }),
  adminSearches: (type) => client.get('/admin/searches', { params: type ? { type } : {} }),
  adminEarnings: () => client.get('/admin/earnings'),
  adminSystemErrors: () => client.get('/admin/system/errors'),
  // ---- Creators & Itineraries ----
  uploadFile: (file, kind = 'image') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    return client.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  mediaUrl: (path) => (path && path.startsWith('/api/') ? `${BACKEND_URL}${path}` : path),
  creatorMe: () => client.get('/creators/me'),
  creatorApplyOptions: () => client.get('/creators/meta/application-options'),
  creatorApply: (body) => client.post('/creators/apply', body),
  creatorResubmit: (body) => client.put('/creators/application', body),
  creatorUpdateProfile: (body) => client.put('/creator/profile', body),
  creatorTripTypes: () => client.get('/creator/meta/trip-types'),
  creatorItineraries: (status) => client.get('/creator/itineraries', { params: status ? { status } : {} }),
  creatorGetItinerary: (id) => client.get(`/creator/itineraries/${id}`),
  creatorCreateItinerary: (body) => client.post('/creator/itineraries', body),
  creatorUpdateItinerary: (id, body) => client.put(`/creator/itineraries/${id}`, body),
  creatorDeleteItinerary: (id) => client.delete(`/creator/itineraries/${id}`),
  creatorSubmitItinerary: (id) => client.post(`/creator/itineraries/${id}/submit`),
  creatorOfferSearch: (params) => client.get('/creator/offers/search', { params }),
  creatorOfferSearchV2: (params) => client.get('/creator/offers/search_v2', { params }),
  creatorOfferVerticals: () => client.get('/creator/offers/verticals'),
  creatorRegisterProduct: (body) => client.post('/creator/products/register', body).then((r) => r).catch(() => null),
  getProduct: (fdId) => client.get(`/products/${fdId}`),
  creatorAnalytics: () => client.get('/creator/analytics'),
  creatorEarnings: () => client.get('/creator/earnings'),
  creatorCampaigns: () => client.get('/creator/campaigns'),
  creatorAvailableCampaigns: () => client.get('/creator/campaigns/available'),
  creatorApplyCampaign: (id, note) => client.post(`/creator/campaigns/${id}/apply`, { note }),
  creatorRespondCampaign: (id, accept) => client.post(`/creator/campaigns/${id}/respond`, { accept }),
  creatorCampaignOptions: () => client.get('/creator/meta/campaign-options'),
  creatorNotifications: () => client.get('/creator/notifications'),
  // public discovery
  discoverItineraries: (params) => client.get('/itineraries', { params }),
  featuredItineraries: (limit = 6) => client.get('/itineraries/featured', { params: { limit } }),
  featuredCreators: (limit = 6) => client.get('/creators/featured', { params: { limit } }),
  creatorsMarketplace: (params) => client.get('/creators', { params }),
  publicCreator: (username) => client.get(`/creators/${username}`),
  publicItinerary: (username, slug) => client.get(`/creators/${username}/itineraries/${slug}`),
  saveItinerary: (id) => client.post(`/itineraries/${id}/save`),
  popularFlights: (currency) => client.get('/flights/popular', { params: { currency: currency || 'INR' } }),
  // editorial (house-authored) itineraries — public + save
  editorialList: (params) => client.get('/editorial/itineraries', { params: params || {} }),
  editorialBySlug: (slug) => client.get(`/editorial/itineraries/${slug}`),
  editorialSave: (slug) => client.post(`/editorial/itineraries/${slug}/save`),
  editorialSaved: () => client.get('/editorial/saved'),
  adminEditorialList: () => client.get('/admin/editorial'),
  adminEditorialGet: (iid) => client.get(`/admin/editorial/${iid}`),
  adminEditorialCreate: (body) => client.post('/admin/editorial', body),
  adminEditorialUpdate: (iid, body) => client.put(`/admin/editorial/${iid}`, body),
  adminEditorialPublish: (iid) => client.post(`/admin/editorial/${iid}/publish`),
  adminEditorialUnpublish: (iid) => client.post(`/admin/editorial/${iid}/unpublish`),
  adminEditorialDelete: (iid) => client.delete(`/admin/editorial/${iid}`),
  adminImageSearch: (q, page) => client.get('/admin/image-search', { params: { q, page: page || 1 } }),
  followCreator: (id) => client.post(`/creators/${id}/follow`),
  itineraryGoUrl: (type, offerId, attr) => {
    const q = new URLSearchParams({ ...attr }).toString();
    return `${API}/go/${type}/${offerId}?${q}`;
  },
  // admin creator management
  adminCreators: (status) => client.get('/admin/creators', { params: status ? { status } : {} }),
  adminCreatorDetail: (id) => client.get(`/admin/creators/${id}`),
  adminAddCreatorNote: (id, note) => client.post(`/admin/creators/${id}/notes`, { note }),
  adminApplications: () => client.get('/admin/creator-applications'),
  adminApproveCreator: (id, body) => client.post(`/admin/creators/${id}/approve`, body || {}),
  adminRejectCreator: (id, body) => client.post(`/admin/creators/${id}/reject`, body || {}),
  adminRequestCreatorChanges: (id, body) => client.post(`/admin/creators/${id}/request-changes`, body || {}),
  adminSuspendCreator: (id) => client.post(`/admin/creators/${id}/suspend`),
  adminVerifyCreator: (id) => client.post(`/admin/creators/${id}/verify`),
  adminModerationQueue: (status) => client.get('/admin/itineraries', { params: status ? { status } : {} }),
  adminGetItinerary: (id) => client.get(`/admin/itineraries/${id}`),
  adminApproveItinerary: (id, body) => client.post(`/admin/itineraries/${id}/approve`, body || {}),
  adminRejectItinerary: (id, body) => client.post(`/admin/itineraries/${id}/reject`, body || {}),
  adminRequestChanges: (id, body) => client.post(`/admin/itineraries/${id}/request-changes`, body || {}),
  adminPublishItinerary: (id, body) => client.post(`/admin/itineraries/${id}/publish`, body || {}),
  adminUnpublishItinerary: (id) => client.post(`/admin/itineraries/${id}/unpublish`),
  adminCampaigns: () => client.get('/admin/campaigns'),
  adminCreateCampaign: (body) => client.post('/admin/campaigns', body),
  // campaign marketplace (templates)
  adminCampaignTemplates: () => client.get('/admin/campaigns/templates'),
  adminCreateCampaignTemplate: (body) => client.post('/admin/campaigns/templates', body),
  adminCampaignTemplateDetail: (id) => client.get(`/admin/campaigns/templates/${id}`),
  adminSetCampaignStatus: (id, status) => client.put(`/admin/campaigns/templates/${id}/status`, { status }),
  adminInviteToCampaign: (id, body) => client.post(`/admin/campaigns/templates/${id}/invite`, body),
  adminCampaignDecision: (id, body) => client.post(`/admin/campaigns/templates/${id}/decision`, body),
  adminSetParticipationStatus: (pid, participation_status) => client.put(`/admin/campaigns/participations/${pid}`, { participation_status }),
  adminSetParticipationPayment: (pid, payment_status) => client.put(`/admin/campaigns/participations/${pid}/payment`, { payment_status }),
  adminPayouts: () => client.get('/admin/payouts'),
  adminAffiliatePerformance: () => client.get('/admin/affiliate-performance'),
  adminRevenueConfig: () => client.get('/admin/revenue-config'),
  adminSetRevenueConfig: (body) => client.put('/admin/revenue-config', body),
  adminConversions: () => client.get('/admin/conversions'),
  partnerPostback: (body) => client.post('/partner/postback', body),
  // ---- Smart Fare Finder ----
  smartFares: (sessionId) => client.get('/flights/smart-fares', { params: { session_id: sessionId } }),
  smartFare: (id) => client.get(`/flights/smart-fares/${id}`),
  trackSmartFare: (event, opp_id, session_id, props) => client.post('/flights/smart-fares/track', { event, opp_id, session_id, props }),
  fiSettings: () => client.get('/admin/fare-intelligence/settings'),
  updateFiSettings: (body) => client.put('/admin/fare-intelligence/settings', body),
  fiDashboard: (includeDemo) => client.get('/admin/fare-intelligence', { params: { include_demo: !!includeDemo } }),
  fiOpportunities: (filters = {}) => client.get('/admin/fare-intelligence/opportunities', { params: filters }),
  fiOpportunityDetail: (id) => client.get(`/admin/fare-intelligence/opportunities/${id}`),
  updateOpportunity: (id, body) => client.put(`/admin/fare-intelligence/${id}`, body),
  // Phase 5 — merchandising / sponsored placements
  fiCampaigns: () => client.get('/admin/fare-intelligence/campaigns'),
  fiSetCampaignTargeting: (id, targeting) => client.put(`/admin/fare-intelligence/campaigns/${id}/targeting`, { smart_fare_targeting: targeting }),
  fiDistributeCampaign: (id) => client.post(`/admin/fare-intelligence/campaigns/${id}/distribute`),
  fiPlacements: (campaign_id, surface) => client.get('/admin/fare-intelligence/placements', { params: { campaign_id: campaign_id || '', surface: surface || '' } }),
  fiSetPlacementStatus: (id, status) => client.put(`/admin/fare-intelligence/placements/${id}`, { status }),
  // Phase 6 — analytics
  fiAnalytics: (params = {}) => client.get('/admin/fare-intelligence/analytics', { params }),
  // Audit
  fiAudit: (entity_id, limit) => client.get('/admin/fare-intelligence/audit', { params: { entity_id: entity_id || '', limit: limit || 200 } }),
  // ---- Content + Merchandising Engine ----
  roleMe: () => client.get('/admin/roles/me'),
  adminStaff: () => client.get('/admin/staff'),
  setStaffRole: (id, role) => client.put(`/admin/staff/${id}/role`, { role }),
  contentMeta: () => client.get('/admin/content-meta'),
  adminMedia: (q) => client.get('/admin/media', { params: q ? { q } : {} }),
  deleteMedia: (fileId) => client.delete(`/admin/media/${fileId}`),
  listContent: (kind, status) => client.get(`/admin/content/${kind}`, { params: status ? { status } : {} }),
  createContent: (kind, data, status) => client.post(`/admin/content/${kind}`, { data, status }),
  updateContent: (kind, id, data, status) => client.put(`/admin/content/${kind}/${id}`, { data, status }),
  setContentStatus: (kind, id, status) => client.put(`/admin/content/${kind}/${id}/status`, { status }),
  archiveContent: (kind, id) => client.post(`/admin/content/${kind}/${id}/archive`),
  adminListDestinations: () => client.get('/admin/destinations'),
  adminDestinations: () => client.get('/admin/destinations'), // alias (fixes prior name mismatch)
  createDestination: (body) => client.post('/admin/destinations', body),
  updateDestination: (slug, body) => client.put(`/admin/destinations/${slug}`, body),
  setDestinationStatus: (slug, status) => client.post(`/admin/destinations/${slug}/status`, { status }),
  featureDestination: (slug, featured) => client.post(`/admin/destinations/${slug}/feature`, { featured }),
  updateDestinationEditorial: (slug, data) => client.put(`/admin/destinations/${slug}/editorial`, data),
  createCampaign: (body) => client.post('/admin/merch/campaigns', body),
  updateCampaign: (id, body) => client.put(`/admin/merch/campaigns/${id}`, body),
  adminListDeals: () => client.get('/admin/deals'),
  updateDealMerch: (id, data) => client.put(`/admin/deals/${id}/merchandising`, data),
  // ---- Country Fare Explorer ----
  countryMeta: () => client.get('/country-fares/meta'),
  countryRegions: (country) => client.get('/country-fares/regions', { params: { country } }),
  countrySearch: (body) => client.post('/country-fares/search', body),
  countrySession: (id) => client.get(`/country-fares/session/${id}`),
  countryTrack: (event, session_id, props) => client.post('/country-fares/track', { event, session_id, props }).catch(() => {}),
  cfSettings: () => client.get('/admin/country-fares/settings'),
  cfUpdateSettings: (body) => client.put('/admin/country-fares/settings', body),
  cfDashboard: () => client.get('/admin/country-fares'),
  cfGeo: (country, type) => client.get('/admin/country-fares/geo', { params: { country: country || '', type: type || 'airport' } }),
  cfAddAirport: (body) => client.post('/admin/country-fares/geo/airport', body),
  cfPatchAirport: (code, body) => client.put(`/admin/country-fares/geo/airport/${code}`, body),
  cfAddCountry: (body) => client.post('/admin/country-fares/geo/country', body),
  // ---- Content + Merchandising (public) ----
  publicHomepage: () => client.get('/content/homepage'),
  publicTrending: (limit) => client.get('/content/trending', { params: { limit: limit || 24 } }),
  publicDestination: (slug) => client.get(`/content/destination/${slug}`),
  publicArticles: (limit) => client.get('/content/articles', { params: { limit: limit || 24 } }),
  // ---- Content + Merchandising (admin) ----
  reorderSections: (order) => client.post('/admin/merch/homepage_sections/reorder', { order }),
  merchCampaigns: () => client.get('/admin/merch/campaigns'),
  distributeCampaign: (id, body) => client.post(`/admin/merch/campaigns/${id}/distribute`, body),
  undistributeCampaign: (id) => client.post(`/admin/merch/campaigns/${id}/undistribute`),
  merchPlacements: () => client.get('/admin/merch/placements'),
  merchItineraries: (status) => client.get('/admin/merch/itineraries', { params: { status: status || '' } }),
  updateItineraryMerch: (id, body) => client.put(`/admin/merch/itineraries/${id}`, body),
  merchAudit: (limit) => client.get('/admin/merch/audit', { params: { limit: limit || 200 } }),
  previewHomepage: () => client.get('/admin/content/preview/homepage'),
  previewTrending: (limit) => client.get('/admin/content/preview/trending', { params: { limit: limit || 24 } }),
  // ---- Visa vertical ----
  visaMeta: () => client.get('/visa/meta'),
  visaCheck: (body) => client.post('/visa/check', body),
  visaTrack: (event, props) => client.post('/visa/track', { event, props }).catch(() => {}),
  // ---- Travel Store ----
  storeMeta: () => client.get('/store/meta'),
  storeProducts: (params) => client.get('/store/products', { params }),
  storeFeatured: () => client.get('/store/featured'),
  storeProduct: (slug) => client.get(`/store/products/${slug}`),
  storeTrack: (event, props) => client.post('/store/track', { event, props }).catch(() => {}),
  storeMatch: (body) => client.post('/admin/store/match', body),
  storeVerifyOffer: (id, confidence) => client.post(`/admin/store/offers/${id}/verify`, { confidence }),
  // Vertical-aware, provider-extensible place search (Hotels/Experiences/Tours — no airports)
  locationsSearch: (q, vertical) => client.get('/locations/search', { params: { q: q || '', vertical } }),
  storeImport: (body) => client.post('/admin/store/import', body),
  storeRecheckOffer: (id) => client.post(`/admin/store/offers/${id}/recheck`),
  adminStoreProducts: () => client.get('/admin/store/products'),
  adminCreateStoreProduct: (body) => client.post('/admin/store/products', body),
  adminSetStoreProductStatus: (id, status) => client.post(`/admin/store/products/${id}/status`, { status }),
  adminFeatureStoreProduct: (id, featured) => client.post(`/admin/store/products/${id}/feature`, { featured }),
  adminCreateStoreOffer: (body) => client.post('/admin/store/offers', body),
  adminStoreReseed: () => client.post('/admin/store/reseed'),
  // ---- Tours vertical ----
  toursMeta: () => client.get('/tours/meta'),
  toursList: (params) => client.get('/tours', { params }),
  toursFeatured: () => client.get('/tours/featured'),
  toursDestinations: () => client.get('/tours/destinations'),
  toursByDestination: (slug, sort) => client.get(`/tours/destination/${slug}`, { params: { sort } }),
  tourResolve: (slug) => client.get(`/tours/resolve/${slug}`),
  tourDetail: (slug) => client.get(`/tours/detail/${slug}`),
  toursTrack: (event, props) => client.post('/tours/track', { event, props }).catch(() => {}),
  adminTours: () => client.get('/admin/tours'),
  adminToursReseed: () => client.post('/admin/tours/reseed'),
  // ---- eSIM vertical ----
  esimMeta: () => client.get('/esim/meta'),
  esimSearch: (q) => client.get('/esim/search', { params: { q } }),
  esimRegions: () => client.get('/esim/regions'),
  esimRegion: (slug, sort) => client.get(`/esim/region/${slug}`, { params: sort ? { sort } : {} }),
  esimCrossSell: (countryCode) => client.get('/esim/cross-sell', { params: { country_code: countryCode } }),
  esimCountry: (slug, sort) => client.get(`/esim/country/${slug}`, { params: sort ? { sort } : {} }),
  esimPlan: (key) => client.get(`/esim/plan/${encodeURIComponent(key)}`),
  esimBuyUrl: (key) => `${API}/esim/buy/${encodeURIComponent(key)}`,
  esimTrack: (event, props) => client.post('/esim/track', { event, props }).catch(() => {}),
  adminEsimProviders: () => client.get('/admin/esim/providers'),
  adminEsimToggleProvider: (id, enabled) => client.post(`/admin/esim/providers/${id}/toggle`, { enabled }),
  adminEsimUpdateProvider: (id, body) => client.put(`/admin/esim/providers/${id}`, body),
  adminEsimSync: () => client.post('/admin/esim/sync'),
  adminEsimReseed: () => client.post('/admin/esim/sync'),
  // ---- Universal Admin Catalog (canonical) ----
  catalogEntities: () => client.get('/admin/catalog/entities'),
  catalogList: (entity, params) => client.get(`/admin/catalog/${entity}`, { params: params || {} }),
  catalogGet: (entity, id) => client.get(`/admin/catalog/${entity}/${id}`),
  catalogCreate: (entity, data) => client.post(`/admin/catalog/${entity}`, { data }),
  catalogUpdate: (entity, id, data) => client.put(`/admin/catalog/${entity}/${id}`, { data }),
  catalogSetStatus: (entity, id, status) => client.post(`/admin/catalog/${entity}/${id}/status`, { status }),
  catalogFeature: (entity, id, featured) => client.post(`/admin/catalog/${entity}/${id}/feature`, { featured }),
  catalogDelete: (entity, id) => client.delete(`/admin/catalog/${entity}/${id}`),
  catalogBulk: (entity, ids, action) => client.post(`/admin/catalog/${entity}/bulk`, { ids, action }),
  catalogOffers: (entity, id) => client.get(`/admin/catalog/${entity}/${id}/offers`),
  catalogCreateOffer: (entity, id, body) => client.post(`/admin/catalog/${entity}/${id}/offers`, body),
  catalogUpdateOffer: (offerId, body) => client.put(`/admin/catalog/offers/${offerId}`, body),
  catalogSetOfferStatus: (offerId, status) => client.post(`/admin/catalog/offers/${offerId}/status`, { status }),
  catalogTestLink: (url) => client.post('/admin/catalog/offers/test-link', { url }),
  catalogAudit: (objectId) => client.get('/admin/catalog/audit', { params: { object_id: objectId || '' } }),
  // ---- Travel Insurance (public, compliance-gated) ----
  insuranceMeta: () => client.get('/insurance/meta'),
  insurancePlans: (params) => client.get('/insurance/plans', { params: params || {} }),
  // ---- Cost & Security Dashboard + Earnings by Source (admin) ----
  securityDashboard: (params) => client.get('/admin/security/dashboard', { params: params || {} }),
  earningsBySource: (params) => client.get('/admin/security/earnings-by-source', { params: params || {} }),
  securityEvents: (params) => client.get('/admin/security/events', { params: params || {} }),
  securityUsage: () => client.get('/admin/security/usage'),
  // ---- Multi-Provider Flight Comparison (additive layer) ----
  flightsCompare: (body) => client.post('/flights/compare', body),
  adminFlightComparison: (params) => client.get('/admin/flights/comparison', { params: params || {} }),
  adminFlightInspect: (params) => client.get('/admin/flights/inspect', { params: params || {} }),
  // ---- Unified location autocomplete (country/region/city/airport) ----
  locationsAutocomplete: (q) => client.get('/locations/autocomplete', { params: { q } }),
  // ---- Admin: providers / health / production readiness ----
  adminProviders: () => client.get('/admin/providers'),
  adminProvidersHealth: (logs) => client.get('/admin/providers/health', { params: { logs: logs || 50 } }),
  adminProductionReadiness: () => client.get('/admin/production-readiness'),
  // ---- Admin: central affiliate analytics ----
  affiliateAnalytics: (params) => client.get('/admin/affiliate/analytics', { params }),
  affiliateClicksList: (params) => client.get('/admin/affiliate/clicks', { params }),
  affiliateClickDetail: (id) => client.get(`/admin/affiliate/clicks/${id}`),
  affiliateExportCsv: (params) => client.get('/admin/affiliate/export.csv', { params, responseType: 'blob' }),
  // Central outbound go link (all verticals: flight/hotel/experience/visa/store)
  goVerticalUrl: (vertical, offerId, attr) =>
    `${API}/go/${vertical}/${offerId}?` + new URLSearchParams({ ...(attr || {}) }).toString(),
  // ---- Provider Registry + Secure Credentials Manager (Epic A + B) ----
  providersOverview: (environment) => client.get('/admin/providers', { params: environment ? { environment } : {} }),
  providerDetail: (id, environment) => client.get(`/admin/providers/${encodeURIComponent(id)}`, { params: environment ? { environment } : {} }),
  providerSetCredentials: (id, body) => client.put(`/admin/providers/${encodeURIComponent(id)}/credentials`, body),
  providerDeleteCredentials: (id, environment) => client.delete(`/admin/providers/${encodeURIComponent(id)}/credentials`, { params: environment ? { environment } : {} }),
  providerTest: (id, environment) => client.post(`/admin/providers/${encodeURIComponent(id)}/test`, { environment }),
  providerSetEnabled: (id, enabled) => client.post(`/admin/providers/${encodeURIComponent(id)}/enable`, { enabled }),
  providerUpdate: (id, body) => client.put(`/admin/providers/${encodeURIComponent(id)}`, body),
  providersAudit: (limit) => client.get('/admin/providers/audit', { params: { limit: limit || 100 } }),
  orchestratorPreview: (vertical, country) => client.get('/admin/providers/orchestrator/preview', { params: { vertical, ...(country ? { country } : {}) } }),
  providerAdapters: () => client.get('/admin/providers/adapters'),
  providerCreate: (body) => client.post('/admin/providers', body),
  providerActivate: (id) => client.post(`/admin/providers/${encodeURIComponent(id)}/activate`),
  providerDeactivate: (id) => client.post(`/admin/providers/${encodeURIComponent(id)}/deactivate`),
  providerArchive: (id) => client.post(`/admin/providers/${encodeURIComponent(id)}/archive`),
  providerRestore: (id) => client.post(`/admin/providers/${encodeURIComponent(id)}/restore`),
  providerDelete: (id) => client.delete(`/admin/providers/${encodeURIComponent(id)}`),
  // ---- RBAC + Employees (Epic 0) ----
  rbacMe: () => client.get('/admin/rbac/me'),
  rbacCatalog: () => client.get('/admin/rbac/catalog'),
  rbacRoles: () => client.get('/admin/rbac/roles'),
  rbacCreateRole: (body) => client.post('/admin/rbac/roles', body),
  rbacUpdateRole: (id, body) => client.put(`/admin/rbac/roles/${id}`, body),
  rbacDeleteRole: (id) => client.delete(`/admin/rbac/roles/${id}`),
  employeesList: () => client.get('/admin/employees'),
  employeeCreate: (body) => client.post('/admin/employees', body),
  employeeUpdate: (id, body) => client.put(`/admin/employees/${id}`, body),
  employeeDeactivate: (id) => client.post(`/admin/employees/${id}/deactivate`),
  employeeActivate: (id) => client.post(`/admin/employees/${id}/activate`),
  employeeResetPassword: (id, new_password) => client.post(`/admin/employees/${id}/reset-password`, { new_password }),
  employeeRevokeSessions: (id) => client.post(`/admin/employees/${id}/revoke-sessions`),
  employeeDelete: (id) => client.delete(`/admin/employees/${id}`),
  employeesAudit: (limit) => client.get('/admin/employees/audit', { params: { limit: limit || 200 } }),
};

export default client;
